var React = require('react');
var { connect } = require('react-redux');
const { useTranslation, withTranslation } = require('react-i18next');
var SensorGraph = require('SensorGraph');
const dayjs = require('../../utils/dayjsConfig');
const { useEffect, useState } = require('react');
var GeneralClasses = require('GeneralClasses');
var { unitDisplayName, convert } = require('unitsManager');
var { loadSamplesData } = require('sensorsManager');

const DischargeGraphContainer = (props) => {
  const [sensor_graph, setSensor_graph] = useState(null);
  const [isDischargeGraph, setIsDischargeGraph] = useState(false);
  const [waterDataState, setWaterDataState] = useState({});
  const { t } = useTranslation();
  let userLengthUnits =
    props.userData && 'length' in props.userData
      ? unitDisplayName(props.userData.length)
      : '';
  let userVolumeUnits =
    props.userData && 'volume' in props.userData
      ? unitDisplayName(props.userData.volume) + '/h'
      : '';

  useEffect(() => {
    if (sensor_graph) {
      setIsDischargeGraph(false);
      let waterMeterData = loadSamplesData(
        props.samples,
        dayjs(props.lastSeen),
        props.pressure_samples,
        props.discharge_samples,
        props.userData,
        setIsDischargeGraph,
      );
      setWaterDataState(waterMeterData);
      scaleGraph(undefined, undefined, waterMeterData);
    }
  }, [
    props.samples,
    sensor_graph,
    props.lastSeen,
    props.pressure_samples,
    props.discharge_samples,
    props.userData,
  ]);

  const changeXAxes = (startDate, endDate) => {
    // Get actual data date range from waterDataState (pressure or discharge)
    // The data is sorted in descending order (newest first)
    let firstDateInData = null;

    if (waterDataState?.pressure?.length > 0) {
      // Last item is oldest (earliest date)
      firstDateInData = dayjs(
        waterDataState.pressure[waterDataState.pressure.length - 1]?.date,
      );
    } else if (waterDataState?.discharge?.length > 0) {
      firstDateInData = dayjs(
        waterDataState.discharge[waterDataState.discharge.length - 1]?.date,
      );
    } else {
      // Fallback to props if no water data
      firstDateInData = dayjs(
        props.samples[props.samples.length - 1]?.end_date,
      );
    }

    // For "back" button: check if the new end date (after going back 7 days)
    // would still overlap with data range
    const newEndDateAfterBack = dayjs(endDate).subtract(7, 'd');

    // We can navigate back if the new window would still show some data
    return newEndDateAfterBack.isSameOrAfter(firstDateInData);
  };

  const scaleGraph = (
    startDate = dayjs(props.lastSeen).subtract(17, 'd').unix(),
    endDate = dayjs(props.lastSeen).unix(),
    waterData = waterDataState,
  ) => {
    // When called from initial load (waterData passed), load all data
    // When called from navigation, just update the chart's x-axis view
    // Don't filter data - just pan the view

    // If waterData is provided (initial load), load all data without filtering
    if (
      waterData &&
      (waterData.pressure?.length > 0 || waterData.discharge?.length > 0)
    ) {
      let framedData = {};
      framedData['pressure'] = waterData.pressure || [];
      framedData['discharge'] = waterData.discharge || [];
      reloadGraph(framedData);
    }

    // Update the chart's x-axis view bounds (pan without reloading data)
    if (sensor_graph?.current_chart) {
      sensor_graph.current_chart.options.scales.x.min = dayjs
        .unix(startDate)
        .toDate();
      sensor_graph.current_chart.options.scales.x.max = dayjs
        .unix(endDate)
        .toDate();

      // Ensure irrigation_hin Y-axis max is preserved after x-axis update
      const convertedHin = convert(
        props?.irrigation_hin,
        'mm',
        props.userData?.length,
        'length',
      );
      if (convertedHin && !isDischargeGraph) {
        // Get current max Y value from the chart data
        let maxYValue = 0;
        sensor_graph.current_chart.data.datasets.forEach((dataset) => {
          dataset.data.forEach((point) => {
            if (point.y > maxYValue) maxYValue = point.y;
          });
        });

        if (maxYValue < convertedHin) {
          sensor_graph.current_chart.options.scales.y.max =
            parseInt(convertedHin) + 50;
        }
      } else if (
        !isDischargeGraph &&
        sensor_graph.current_chart?.options?.scales?.y
      ) {
        sensor_graph.current_chart.options.scales.y.suggestedMin = 0;
        sensor_graph.current_chart.options.scales.y.suggestedMax = 50;
        sensor_graph.current_chart.options.scales.y.ticks.stepSize = 10;
      }

      sensor_graph.current_chart.update();
    }
  };

  const reloadGraph = (waterMeterData) => {
    let dataToDisplay = {};
    sensor_graph.loadData(null, null, undefined, undefined);
    if (waterMeterData['pressure']) {
      dataToDisplay['pressure'] = new GeneralClasses.Sensor(
        {
          name: 'pressureValues',
          displayName: 'Pressure',
          data: waterMeterData['pressure'],
          color: '#662F6F',
          coordinates: { latitude: '23.2222', longitude: '23.2222' },
          settings: { is_active: true },
        },
        'pressureValues',
      );
    }
    if (waterMeterData['discharge']) {
      dataToDisplay['discharge'] = new GeneralClasses.Sensor(
        {
          name: 'dischargeValues',
          displayName: 'Discharge',
          data: waterMeterData['discharge'],
          color: '#3A8500',
          coordinates: { latitude: '23.2222', longitude: '23.2222' },
          settings: { is_active: true },
        },
        'dischargeValues',
      );
    }
    sensor_graph.loadData(dataToDisplay, {}, 0, props.time_zone, {}, {});
  };

  return (
    <div className='d-flex flex-column w-100 h-100'>
      <div className='d-flex flex-wrap '>
        <div className='d-flex pl-lg-0 text-size-16 font-weight-bold align-items-center ml-3 text-grey45'>
          {isDischargeGraph
            ? t('discharge over time')
            : t('Pressure over time')}
        </div>
      </div>
      <div className='flex-grow-1'>
        <div className='d-flex h-100'>
          <div className='flex-fill' style={{ width: '40%' }}>
            <SensorGraph
              onRef={(ref) => setSensor_graph(ref)}
              onGraphUpdate={() => {}}
              units={isDischargeGraph ? userVolumeUnits : userLengthUnits}
              isLoading={false}
              shouldDrawTodayMark={false}
              shouldDrawBottom={false}
              shouldDrawInputs={false}
              shouldDiffMode={true}
              shouldSetMinMaxXAxes={true}
              toolTipMode={'nearest'}
              distributionMode={'linear'}
              shouldDisplayZoom={false}
              shouldSpaceData={false}
              irrigation_hin={convert(
                props?.irrigation_hin,
                'mm',
                props.userData?.length,
                'length',
              )}
              shouldDrawResetButton={false}
              shouldDiplayUTC={true}
              shouldScaleGraph={true}
              scaleGraph={scaleGraph}
              changeXAxes={changeXAxes}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = function (state) {
  return {
    userData: state.authentication_data.user_data,
  };
};
const mapDispatchToProps = {};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(DischargeGraphContainer));
